/**
 * AnalysisCard - Compact card with "View" button for detail modal
 * Supports compact (grid) and default (list) modes
 */

import { Phone, MessageSquare, Eye, User, Clock } from 'lucide-react';
import SentimentBadge from './SentimentBadge';
import LocationPill from './LocationPill';
import AgentName from '../shared/AgentName';
import { toTitleCase, typeLabel, sentimentToPercent } from '../../utils/formatters';
import { onActivateKey } from '../../utils/a11y';

const LANG_NAMES = {
  en: 'English', hi: 'Hindi', bn: 'Bengali', ta: 'Tamil', te: 'Telugu',
  mr: 'Marathi', gu: 'Gujarati', kn: 'Kannada', ml: 'Malayalam', pa: 'Punjabi',
  ur: 'Urdu', or: 'Odia', as: 'Assamese', ne: 'Nepali', sa: 'Sanskrit',
  es: 'Spanish', fr: 'French', de: 'German', ar: 'Arabic', zh: 'Chinese',
  ja: 'Japanese', ko: 'Korean', pt: 'Portuguese', ru: 'Russian', it: 'Italian',
};

const BORDER_COLORS = {
  very_positive: 'border-l-green-600',
  positive: 'border-l-green-400',
  neutral: 'border-l-slate-400',
  negative: 'border-l-red-400',
  very_negative: 'border-l-red-600',
};

const formatDuration = (seconds) => {
  if (!seconds) return null;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const DISPOSITION_BADGE = {
  resolved: 'bg-green-100 text-green-700',
  escalated: 'bg-red-100 text-red-700',
  transferred: 'bg-blue-100 text-blue-700',
  abandoned: 'bg-gray-100 text-gray-600',
  follow_up_required: 'bg-yellow-100 text-yellow-700',
  pending: 'bg-gray-50 text-gray-500',
};

const AnalysisCard = ({ analysis, compact = false, onViewDetails, location }) => {
  const sentiment = analysis.overall_sentiment || 'neutral';
  const summary = analysis.summary;
  const contactReason = analysis.contact_reason;
  const disposition = analysis.disposition;
  const lang = analysis.language;
  const borderColor = BORDER_COLORS[sentiment] || 'border-l-gray-300';
  const TypeIcon = analysis.interaction_type === 'call' ? Phone : MessageSquare;
  const scorePct = sentimentToPercent(analysis.sentiment_score);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={onActivateKey(() => onViewDetails?.(analysis))}
      className={`card border-l-4 ${borderColor} overflow-hidden hover:shadow-lg hover:bg-gray-50 transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-300`}
      onClick={() => onViewDetails?.(analysis)}
    >
      <div className="px-5 py-4">
        {/* Header row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TypeIcon size={14} className="text-gray-400" />
            <span className="text-xs font-mono text-gray-500">#{analysis.interaction_id}</span>
            <SentimentBadge sentiment={sentiment} />
            <span className="text-xs text-gray-600">{scorePct}/100</span>
          </div>
          <div className="flex items-center space-x-2">
            <LocationPill location={location} />
            {lang && lang !== 'en' && (
              <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-mono uppercase" title={LANG_NAMES[lang?.toLowerCase()] || lang}>{lang}</span>
            )}
          </div>
        </div>

        {/* Summary — truncated */}
        {summary && (
          <p className={`text-sm text-gray-700 leading-relaxed mt-1.5 ${compact ? 'line-clamp-2' : 'line-clamp-3'}`}>
            {summary}
          </p>
        )}

        {/* Agent + Reason */}
        <div className="flex items-center gap-1.5 mt-1.5">
          {analysis.agent_id && (
            <span className="flex items-center space-x-1 text-xs text-gray-500">
              <User size={10} className="text-gray-400" />
              <AgentName id={analysis.agent_id} />
            </span>
          )}
          {contactReason && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-medium">
              {toTitleCase(contactReason)}
            </span>
          )}
          {disposition && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${DISPOSITION_BADGE[disposition] || DISPOSITION_BADGE.pending}`}>
              {toTitleCase(disposition)}
            </span>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-2">
            <span className="flex items-center space-x-1 text-xs text-gray-400">
              <Clock size={11} />
              <span>
                {(analysis.occurred_at || analysis.analyzed_at)
                  ? new Date(analysis.occurred_at || analysis.analyzed_at).toLocaleString()
                  : 'N/A'}
              </span>
            </span>
            {analysis.duration_seconds > 0 && (
              <span className="text-xs text-gray-400">{formatDuration(analysis.duration_seconds)}</span>
            )}
            <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-50 text-cyan-700 font-medium"
                  title={analysis.source_id && analysis.source_id !== analysis.interaction_id ? `Source: ${analysis.source_id}` : ''}>
              {toTitleCase(typeLabel(analysis.source_type || 'platform'))}
            </span>
          </div>
          <button
            onClick={(e) => { e.stopPropagation(); onViewDetails?.(analysis); }}
            className="flex items-center space-x-1 bg-blue-50 text-blue-600 px-2.5 py-1 rounded-md text-xs font-medium hover:bg-blue-100 transition-colors"
          >
            <Eye size={12} />
            <span>View</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AnalysisCard;
